<template>
  <div></div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, shallowRef } from 'vue'
import * as THREE from 'three'

defineOptions({
  name: 'RadarPlayer3D_ThickAxes',
})

interface Props {
  length?: number
  radius?: number
}

const props = withDefaults(defineProps<Props>(), {
  length: 5,
  radius: 0.04,
})

// 着色器定义
const thickAxesShaders = {
  vertex: /* glsl */ `
    uniform vec3 uAxisDir;
    uniform float uAxisLen;

    varying float vAlong;

    void main() {
      // 沿轴方向的投影，用于渐变
      float proj = dot(position, uAxisDir);
      vAlong = clamp(proj / uAxisLen, 0.0, 1.0);

      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,

  fragment: /* glsl */ `
    precision highp float;

    uniform vec3 uColor;
    uniform float uGlow;

    varying float vAlong;

    void main() {
      // 根部暗、末端亮（类似 AxesHelper 的亮度变化）
      float intensity = mix(0.35, 1.0, vAlong);
      vec3 col = uColor * intensity * uGlow;

      // 发光外壳通常更透明
      float alpha = mix(0.15, 0.85, vAlong);

      gl_FragColor = vec4(col, alpha);
      if (gl_FragColor.a < 0.001) discard;
    }
  `,
}

// 创建主组 - 使用shallowRef避免Vue深度代理Three.js对象
const group = shallowRef(new THREE.Group())

// 导出group，供父组件使用
defineExpose({
  group,
})

// 轴端点
const origin = ref(new THREE.Vector3(0, 0, 0))
const xEnd = ref(new THREE.Vector3(props.length, 0, 0))
const yEnd = ref(new THREE.Vector3(0, props.length, 0))
const zEnd = ref(new THREE.Vector3(0, 0, props.length))

// 颜色定义
const AXIS_COLORS = {
  x: 0xff2222,
  y: 0x22ee22,
  z: 0x2255ee,
}

// 创建所有轴管
const createAxes = () => {
  // 清空现有轴管
  while (group.value.children.length > 0) {
    group.value.remove(group.value.children[0])
  }

  // 创建X轴
  createAxisTube(group.value, origin.value, xEnd.value, AXIS_COLORS.x, props.radius)
  // 创建Y轴
  createAxisTube(group.value, origin.value, yEnd.value, AXIS_COLORS.y, props.radius)
  // 创建Z轴
  createAxisTube(group.value, origin.value, zEnd.value, AXIS_COLORS.z, props.radius)
}

// 更新轴长度
watch(
  () => props.length,
  (newLength) => {
    xEnd.value.set(newLength, 0, 0)
    yEnd.value.set(0, newLength, 0)
    zEnd.value.set(0, 0, newLength)
    createAxes()
  }
)

// 更新轴半径
watch(
  () => props.radius,
  () => {
    createAxes()
  }
)

// 挂载时创建轴管
onMounted(() => {
  createAxes()
})

// 轴管组件 - 创建轴管并添加到父组
const createAxisTube = (
  parentGroup: THREE.Group,
  from: THREE.Vector3,
  to: THREE.Vector3,
  color: number,
  radius: number = 0.04
) => {
  const curve = new THREE.LineCurve3(from, to)
  const tubularSegments = 1
  const radialSegments = 16
  const glowRadius = radius * 1.25
  const glowStrength = 1.45

  // 内核几何
  const coreGeometry = new THREE.TubeGeometry(curve, tubularSegments, radius, radialSegments, false)

  // 发光外壳几何
  const glowGeometry = new THREE.TubeGeometry(
    curve,
    tubularSegments,
    glowRadius,
    radialSegments,
    false
  )

  // 内核材质
  const coreMaterial = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: 1.0,
    depthWrite: true,
  })

  // 发光外壳材质
  const axisDir = new THREE.Vector3().subVectors(to, from)
  const axisLen = axisDir.length() || 1.0
  const c = new THREE.Color(color)

  const glowMaterial = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    uniforms: {
      uColor: { value: new THREE.Vector3(c.r, c.g, c.b) },
      uGlow: { value: glowStrength },
      uAxisDir: { value: axisDir.clone().normalize() },
      uAxisLen: { value: axisLen },
    },
    vertexShader: thickAxesShaders.vertex,
    fragmentShader: thickAxesShaders.fragment,
  })

  // 创建发光外壳网格并添加到父组
  const glowMesh = new THREE.Mesh(glowGeometry, glowMaterial)
  parentGroup.add(glowMesh)

  // 创建内核网格并添加到父组
  const coreMesh = new THREE.Mesh(coreGeometry, coreMaterial)
  parentGroup.add(coreMesh)
}
</script>

<style scoped lang="less"></style>
